import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import DeleteActionButton from '../../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../../components/designe/ActionButtons/EditActionButton';
import ConfirmDialog from '../../../../components/reusable/confirm-dialog/ConfirmDialog';
import { useGetAvailableExercises } from '../../../../redux/api/exercisesAPI';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import { uniqBy } from '../../../../utils/collections/array-utils';
import { CurrentTrainingSet } from '../current-training-set/CurrentTrainingSet';
import { useStyles } from './CurrentTrainingSetGroup.styles';
import { useCurrentTrainingSetGroup } from './useCurrentTrainingSetGroup';

interface ICurrentTrainingSetGroupProps {
  onEditTrainingSetClick: (a: ITrainingSet[]) => void;
  trainingSetGroup: ITrainingSet[];
  trainingId: string;
  activateTimer: (value: number) => void;
}

export default function CurrentTrainingSetGroup(
  props: ICurrentTrainingSetGroupProps,
) {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const { data: availableExercises } = useGetAvailableExercises();
  const exercises = availableExercises || [];

  const {
    trainingSetGroup,
    trainingId,
    activateTimer,
    onEditTrainingSetClick,
  } = props;

  const { setGroupDeleting, setSetGroupDeleting, onRemoveSetGroup } =
    useCurrentTrainingSetGroup(trainingSetGroup, trainingId);

  const isSuperset = trainingSetGroup.length > 1;

  const viewName = useMemo(() => {
    const uniqueExerciseTitles = uniqBy<ITrainingSet>(
      trainingSetGroup,
      'exerciseId',
    )
      .map((set) => exercises.find((e) => e.id === set?.exerciseId)?.title)
      .filter(Boolean)
      .join(', ');

    return uniqueExerciseTitles ? (
      <Typography variant="h3" color={theme.palette.font.secondary}>
        {isSuperset && (
          <Typography
            variant="h3"
            fontWeight={600}
            color={theme.palette.font.secondary}
            component="span"
            marginRight={0.5}>
            {t('setTable.superset')}:
          </Typography>
        )}
        {uniqueExerciseTitles}
      </Typography>
    ) : null;
  }, [trainingSetGroup, exercises]);

  const groupActions = useMemo(
    () => (
      <Box display="flex" gap={1}>
        <EditActionButton
          data-onboarding="currentTraining.editSet"
          onClick={() => onEditTrainingSetClick(trainingSetGroup)}
        />
        <DeleteActionButton onClick={() => setSetGroupDeleting(true)} />
      </Box>
    ),
    [onEditTrainingSetClick, setSetGroupDeleting, trainingSetGroup],
  );

  return (
    <Box className={classes.trainingSetGroup}>
      {isSuperset && (
        <Box className={classes.setGroupHeader}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            {viewName}
            {groupActions}
          </Box>
        </Box>
      )}
      {trainingSetGroup.map((trainingSet: ITrainingSet, index: number) => (
        <React.Fragment key={trainingSet.id}>
          {isSuperset && (
            <Box
              className={classes.trainingSetWrapperDivider}
              marginTop={index > 0 ? theme.spacing(2) : 0}
            />
          )}
          <CurrentTrainingSet
            trainingSet={trainingSet}
            trainingId={trainingId}
            activateTimer={activateTimer}
            groupActions={!isSuperset ? groupActions : undefined}
          />
        </React.Fragment>
      ))}

      <ConfirmDialog
        open={!!setGroupDeleting}
        onClose={() => setSetGroupDeleting(false)}
        title={t('common.delete')}
        onSubmit={() => {
          onRemoveSetGroup();
        }}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
    </Box>
  );
}
